import { Injectable } from '@angular/core';
import { Resolve, ActivatedRouteSnapshot, RouterStateSnapshot, UrlSegment, Router } from '@angular/router';
import { Observable, throwError, of } from 'rxjs';
import { map, catchError, switchMap, flatMap } from 'rxjs/operators';

import { IQuotationsQuotation, IQuotationBlock, IQuotationSection } from 'libraries/service/resources/quotation-quotations/IQuotationsQuotation';
import { CachedQuotationService } from '../cached-quotation.service';
import { TabCode } from 'app/shared/model/ITab';
import { Yii } from 'libraries/interfaces/Yii';

export interface IQuotationResolved { hasOptions: boolean; quotation: IQuotationsQuotation; }


@Injectable({
    providedIn: 'root'
})
export class QuotationResolve implements Resolve<IQuotationResolved | string | boolean> {
    constructor(
        private readonly cachedQuotationService: CachedQuotationService,
        private readonly router: Router
    ) {}

    public resolve(activatedRouteSnapshot: ActivatedRouteSnapshot): Observable<IQuotationResolved | string | boolean> {
        const id: number = +activatedRouteSnapshot.paramMap.get('id');

        return of(id).pipe(
            switchMap((quotationId: number) => {
                if (isNaN(quotationId)) {
                    return throwError({ message: Yii.t('wui-quotation-js', "L'id de cotation n'est pas correct") });
                }

                return this.cachedQuotationService.get(quotationId);
            }),
            flatMap((quotation: IQuotationsQuotation) => {
                const blocks: IQuotationBlock[] = quotation.sections.reduce((sectionBlocks: IQuotationBlock[], section: IQuotationSection) => {
                    if (section.blocks) {
                        return [
                            ...sectionBlocks,
                            ...section.blocks
                        ];
                    }

                    return sectionBlocks;
                }, []);

                const hasOptions: boolean = blocks.some((block: IQuotationBlock) => block && block.isOption);
                const url: UrlSegment[] = activatedRouteSnapshot.url;

                if (!hasOptions && url.some(({path}: UrlSegment) => path === TabCode.option)) {
                    const newPath: string = url.map(({path}: UrlSegment) => {
                        if (path === TabCode.option) {
                            return TabCode.included;
                        }

                        return path;
                    }).join('/');

                    return this.router.navigateByUrl(`/quotation/${newPath}`);
                }

                return of({ hasOptions, quotation });
            }),
            catchError((error: Error) => error.message)
        );
    }
}
